import { useMemo, useState, useEffect, RefObject } from 'react';
import { connect } from 'react-redux';

import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { Sidebar } from 'CorePackage/MainModule/components/Sidebar';
import { ROUTE_DECISION } from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';

import { DecisionMenuItemsIdsEnum } from '../enums/DecisionMenuItemsIdsEnum';
import { IDecisionMenuItem } from '../interfaces/IDecisionMenuItem';
import { basicDecisionMenuItems } from '../constants/basicDecisionMenuItems';
import { getDecisionDataCreatedAt, getDecisionUrlId } from '../selectors/decisionSelectors';

interface IAnchor {
  route: string;
  currentAnchorElement: HTMLElement | null;
  nextAnchorElement: HTMLElement | null;
}

interface IKnmPmMenuProps {
  dataRef: RefObject<HTMLDivElement>;
  hiddenItemsIds?: DecisionMenuItemsIdsEnum[];
}

interface IDecisionMenuStoreProps {
  decisionUrlId: string | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

// Duplicated, after clarification need implementation of common MenuModule
const DecisionMenuWithoutStore = ({
  dataRef,
  hiddenItemsIds,
  decisionUrlId,
  created,
  settings,
}: IKnmPmMenuProps & IDecisionMenuStoreProps) => {
  const [locationHash, setLocationHash] = useState(window.location.hash);
  const menuItems = useMemo(
    () =>
      basicDecisionMenuItems(created, settings).reduce((newMenuItems: IDecisionMenuItem[], basicMenuItem) => {
        if (hiddenItemsIds?.some(hiddenItemId => hiddenItemId === basicMenuItem.id)) {
          return newMenuItems;
        }

        newMenuItems.push(basicMenuItem);

        return newMenuItems;
      }, []),
    [hiddenItemsIds],
  );

  const [anchors, setAnchors] = useState<IAnchor[]>([]);

  useEffect(() => {
    setAnchors(
      menuItems.map((item, i) => {
        const isLast = menuItems.length === i + 1;
        const anchorId = item.route.replace('#', '');
        const currentAnchorElement = document.getElementById(anchorId);
        const nextAnchorId = !isLast ? menuItems[i + 1].route.replace('#', '') : null;
        const nextAnchorElement = nextAnchorId ? document.getElementById(nextAnchorId) : null;

        return {
          route: item.route,
          currentAnchorElement,
          nextAnchorElement,
        };
      }),
    );
  }, [menuItems]);

  useEffect(() => {
    const updateLocationHash = () => {
      const refOffset = dataRef.current?.getBoundingClientRect().top;

      if (refOffset || refOffset === 0) {
        if (refOffset < HEADER_STICKED_HEIGHT) {
          const isPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;

          if (isPageBottom && anchors.length > 0) {
            const lastRoute = anchors[anchors.length - 1].route;

            if (window.location.hash !== lastRoute) {
              window.history.replaceState(null, '', lastRoute);
            }
          } else {
            anchors.forEach(({ route, currentAnchorElement, nextAnchorElement }) => {
              const currentAnchorOffsetTop = currentAnchorElement?.getBoundingClientRect().top!;
              const nextAnchorOffsetTop = nextAnchorElement?.getBoundingClientRect().top!;

              if (
                currentAnchorOffsetTop <= HEADER_STICKED_HEIGHT &&
                nextAnchorOffsetTop > HEADER_STICKED_HEIGHT &&
                window.location.hash !== route
              ) {
                window.history.replaceState(null, '', route);
              }
            });
          }
        } else if (window.location.hash && decisionUrlId) {
          window.history.replaceState(null, '', ROUTE_DECISION(decisionUrlId));
        }
      }
      setLocationHash(window.location.hash);
    };

    updateLocationHash();

    window.addEventListener('scroll', updateLocationHash);

    return () => window.removeEventListener('scroll', updateLocationHash);
  }, [anchors, decisionUrlId, dataRef]);

  return <Sidebar type="knm-menu" menuItems={menuItems} locationHash={locationHash} itemsIsAnchors />;
};

const mapStateToProps = (state: IRootState): IDecisionMenuStoreProps => ({
  decisionUrlId: getDecisionUrlId(state),
  created: getDecisionDataCreatedAt(state),
  settings: getAdminFetchSettings(state),
});

export const DecisionMenu = connect(mapStateToProps)(DecisionMenuWithoutStore);
